import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLazyQuery } from '@apollo/react-hooks';
import { gql } from 'apollo-boost';

import Button from './Button';
import Input from './Input';
import Loader from './Loader';
import Table from './Table';

const GET_VISIBLE_SURVEYS_FOR_USER = gql`
  query getVisibleSurveysForUser($email: String!, $language: String) {
    getVisibleSurveysForUser(email: $email, language: $language) {
      surveys {
        surveyId
        name
        isVisible
        reason
        language
        isCommunityOnly
        generalCommunity
      }
      allSurveysVisible
    }
  }
`;

const SurveyVisibilityChecker = () => {
  const [email, setEmail] = useState('');
  const [originalSurveys, setOriginalSurveys] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [page, setPage] = useState(0);
  const [language, setLanguage] = useState('all');
  const [
    showOnlyNonCommunitySurveys,
    setShowOnlyNonCommunitySurveys
  ] = useState(false);
  const [showOnlyVisibleSurveys, setShowOnlyVisibleSurveys] = useState(false);
  const [allSurveysVisible, setAllSurveysVisible] = useState(false);

  const [fetchSurveys, { loading, error }] = useLazyQuery(
    GET_VISIBLE_SURVEYS_FOR_USER,
    {
      fetchPolicy: 'network-only',
      onCompleted: response => {
        const result = response && response.getVisibleSurveysForUser;
        setOriginalSurveys((result && result.surveys) || []);
        setAllSurveysVisible((result && result.allSurveysVisible) || false);
      }
    }
  );

  const handleSearch = useCallback(
    () => {
      const lang = language === 'all' ? undefined : language;
      fetchSurveys({ variables: { email, language: lang } });
    },
    [email, language, fetchSurveys]
  );

  useEffect(
    () => {
      let filteredSurveys = [...originalSurveys];

      if (showOnlyNonCommunitySurveys) {
        filteredSurveys = filteredSurveys.filter(
          survey =>
            !survey.isCommunityOnly ||
            (allSurveysVisible && survey.generalCommunity)
        );
      }

      if (showOnlyVisibleSurveys) {
        filteredSurveys = filteredSurveys.filter(survey => survey.isVisible);
      }

      setSurveys(filteredSurveys);
    },
    [
      originalSurveys,
      showOnlyNonCommunitySurveys,
      showOnlyVisibleSurveys,
      allSurveysVisible
    ]
  );

  const handleEmailChange = e => {
    setEmail(e.target.value);
  };

  const headers = [
    { key: 'surveyId', label: 'Survey ID' },
    { key: 'name', label: 'Name' },
    { key: 'language', label: 'Language' },
    { key: 'isCommunityOnly', label: 'Community Only' },
    { key: 'isVisible', label: 'Visible' },
    { key: 'reason', label: 'Reason' }
  ];

  const renderRow = survey => (
    <tr key={survey.surveyId}>
      <td>{survey.surveyId}</td>
      <td>{survey.name}</td>
      <td>{survey.language}</td>
      <td>{survey.isCommunityOnly ? 'Yes' : 'No'}</td>
      <td>{survey.isVisible ? 'Yes' : 'No'}</td>
      <td>{survey.reason || '-'}</td>
    </tr>
  );

  /* eslint-disable */
  return (
    <div style={styles.container}>
      <Link to="/development-dashboard" style={styles.backLink}>
        &larr; Back to dashboard
      </Link>
      <h1>Survey Visibility Checker</h1>
      <p>Check which surveys are visible for a specific user.</p>

      <div style={styles.inputGroup}>
        <Input
          type="email"
          value={email}
          onChange={handleEmailChange}
          placeholder="Enter user email"
          style={styles.inputField}
        />
        <Button onClick={handleSearch} primary style={styles.searchButton}>
          Search Surveys
        </Button>
      </div>

      <div style={styles.filterGroup}>
        <select
          value={language}
          onChange={e => setLanguage(e.target.value)}
          style={styles.dropdown}
        >
          <option value="all">All Languages</option>
          <option value="Dutch">Dutch</option>
          <option value="French">French</option>
        </select>
        <label style={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={showOnlyNonCommunitySurveys}
            onChange={e => setShowOnlyNonCommunitySurveys(e.target.checked)}
          />
          Show only non community surveys
        </label>
        <label style={{ ...styles.checkboxLabel, marginLeft: '20px' }}>
          <input
            type="checkbox"
            checked={showOnlyVisibleSurveys}
            onChange={e => setShowOnlyVisibleSurveys(e.target.checked)}
          />
          Show only visible surveys
        </label>
      </div>

      {loading && <Loader isProcessing />}
      {error && <p style={styles.errorText}>Error: {error.message}</p>}

      {surveys.length > 0 && (
        <div style={styles.tableContainer}>
          <h2>Survey Visibility Results</h2>
          <Table
            headers={headers}
            data={surveys}
            renderRow={renderRow}
            pagination={{
              page,
              setPage,
              total: surveys.length,
              limit: 20
            }}
          />
        </div>
      )}
    </div>
  );
  /* eslint-enable */
};

const styles = {
  container: {
    padding: '20px'
  },
  backLink: {
    display: 'inline-block',
    marginBottom: '10px',
    color: '#1f6fb2',
    textDecoration: 'none',
    fontWeight: 600
  },
  inputGroup: {
    display: 'flex',
    marginBottom: '10px'
  },
  filterGroup: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px'
  },
  dropdown: {
    marginRight: '20px',
    padding: '8px',
    borderRadius: '4px',
    border: '1px solid #ccc'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer'
  },
  inputField: {
    marginRight: '10px',
    flexGrow: 1
  },
  searchButton: {
    minWidth: '150px'
  },
  errorText: {
    color: 'red'
  },
  tableContainer: {
    marginTop: '20px'
  }
};

export default SurveyVisibilityChecker;
